// ============================================================
// Moteur de thèmes « React » — schéma de blocs.
//
// En plus du HTML/CSS classique, le moteur de rendu accepte
// désormais un contenu de page décrit sous forme de BLOCS (JSON).
// Chaque bloc correspond à un vrai COMPOSANT React rendu par le
// moteur : les blocs dynamiques (produits, services, formulaires…)
// réutilisent les composants du moteur de balises Zifek.
//
// Format accepté (au choix) :
//   { "blocks": [ { "type": "hero", "props": { ... } }, ... ] }
//   [ { "type": "products", "props": { "limit": 6 } }, ... ]
//
// Cette approche est sûre : aucune exécution de code arbitraire,
// uniquement une liste de blocs connus rendus en React.
// ============================================================

export interface ThemeBlock {
  type: string;
  props?: Record<string, unknown>;
}

export interface ThemeBlockSchema {
  blocks: ThemeBlock[];
}

/** Catalogue des blocs disponibles (pour l'éditeur visuel). */
export interface BlockCatalogEntry {
  type: string;
  label: string;
  icon: string;
  category: 'structure' | 'contenu' | 'dynamique' | 'marketplace';
  description: string;
  defaultProps: Record<string, unknown>;
}

export const BLOCK_CATALOG: BlockCatalogEntry[] = [
  { type: 'hero', label: 'Hero (bannière)', icon: 'ri-layout-top-line', category: 'structure', description: 'Bannière d’accueil avec titre, sous-titre et boutons', defaultProps: { title: '{{site_name}}', subtitle: '{{site_description}}', ctaLabel: 'Découvrir', ctaHref: '/produits', cta2Label: 'Nous contacter', cta2Href: '/contact' } },
  { type: 'heading', label: 'Titre', icon: 'ri-heading', category: 'contenu', description: 'Titre de section', defaultProps: { text: 'Notre sélection', level: 2, align: 'left' } },
  { type: 'text', label: 'Texte', icon: 'ri-text', category: 'contenu', description: 'Paragraphe de texte', defaultProps: { html: '{{site_about}}', align: 'left' } },
  { type: 'image', label: 'Image', icon: 'ri-image-line', category: 'contenu', description: 'Image avec texte alternatif', defaultProps: { src: '', alt: '', radius: 16 } },
  { type: 'tabs', label: 'Onglets', icon: 'ri-folders-line', category: 'contenu', description: 'Contenu organisé en onglets cliquables', defaultProps: { items: [{ title: 'Onglet 1', html: '{{site_about}}' }, { title: 'Onglet 2', html: '{{site_description}}' }, { title: 'Onglet 3', html: '<p>Ajoutez votre contenu ici.</p>' }] } },
  { type: 'before_after', label: 'Avant / Après', icon: 'ri-contrast-drop-2-line', category: 'contenu', description: 'Comparateur d’images avant/après avec curseur', defaultProps: { imageBefore: '', imageAfter: '', labelBefore: 'Avant', labelAfter: 'Après' } },
  { type: 'trust_banner', label: 'Bandeau de confiance', icon: 'ri-shield-star-line', category: 'structure', description: 'Rassurance : paiement, livraison, support…', defaultProps: { items: [{ icon: 'ri-shield-check-line', label: 'Paiement sécurisé' }, { icon: 'ri-truck-line', label: 'Livraison rapide' }, { icon: 'ri-customer-service-2-line', label: 'Support 7j/7' }, { icon: 'ri-refund-2-line', label: 'Satisfait ou remboursé' }] } },
  { type: 'cta', label: 'Bloc CTA', icon: 'ri-megaphone-line', category: 'structure', description: 'Bloc d’appel à l’action', defaultProps: { title: 'Prêt à commencer ?', text: 'Découvrez nos offres.', buttonLabel: 'Nous contacter', buttonHref: '/contact' } },
  { type: 'spacer', label: 'Espace', icon: 'ri-space', category: 'structure', description: 'Espace vertical', defaultProps: { height: 48 } },
  { type: 'divider', label: 'Séparateur', icon: 'ri-separator', category: 'structure', description: 'Ligne de séparation', defaultProps: {} },
  { type: 'products', label: 'Produits', icon: 'ri-shopping-bag-3-line', category: 'dynamique', description: 'Grille de produits du marchand', defaultProps: { limit: 6 } },
  { type: 'featured_products', label: 'Produits vedettes', icon: 'ri-star-line', category: 'dynamique', description: 'Produits mis en avant', defaultProps: { limit: 6 } },
  { type: 'services', label: 'Services', icon: 'ri-service-line', category: 'dynamique', description: 'Liste des services', defaultProps: {} },
  { type: 'testimonials', label: 'Témoignages', icon: 'ri-chat-quote-line', category: 'dynamique', description: 'Avis clients', defaultProps: {} },
  { type: 'stats', label: 'Statistiques', icon: 'ri-bar-chart-box-line', category: 'dynamique', description: 'Chiffres clés', defaultProps: {} },
  { type: 'faq', label: 'FAQ', icon: 'ri-question-answer-line', category: 'dynamique', description: 'Questions fréquentes', defaultProps: {} },
  { type: 'gallery', label: 'Galerie', icon: 'ri-gallery-line', category: 'dynamique', description: 'Galerie d’images', defaultProps: { limit: 8 } },
  { type: 'blog_posts', label: 'Articles', icon: 'ri-article-line', category: 'dynamique', description: 'Derniers articles', defaultProps: {} },
  { type: 'partners', label: 'Partenaires', icon: 'ri-links-line', category: 'dynamique', description: 'Logos partenaires', defaultProps: {} },
  { type: 'team', label: 'Équipe', icon: 'ri-team-line', category: 'dynamique', description: 'Membres de l’équipe', defaultProps: {} },
  { type: 'portfolio', label: 'Portfolio', icon: 'ri-folder-image-line', category: 'dynamique', description: 'Réalisations', defaultProps: {} },
  { type: 'hebergements', label: 'Hébergements', icon: 'ri-hotel-line', category: 'dynamique', description: 'Biens d’hébergement', defaultProps: { limit: 6 } },
  { type: 'hebergement_search', label: 'Recherche hébergement', icon: 'ri-search-eye-line', category: 'dynamique', description: 'Recherche + filtres par type d’hébergement (hôtel, riad, villa…)', defaultProps: { limit: 6 } },
  { type: 'events', label: 'Événements', icon: 'ri-calendar-event-line', category: 'dynamique', description: 'Agenda / événements du commerce', defaultProps: { limit: 6 } },
  { type: 'formations', label: 'Formations', icon: 'ri-graduation-cap-line', category: 'dynamique', description: 'Formations en ligne avec inscription et paiement', defaultProps: { limit: 6 } },
  { type: 'categories', label: 'Catégories', icon: 'ri-apps-2-line', category: 'dynamique', description: 'Catégories produits, services et hébergements', defaultProps: {} },
  { type: 'restauration', label: 'Restauration', icon: 'ri-restaurant-2-line', category: 'dynamique', description: 'Carte du restaurant (plats, menus, boissons)', defaultProps: { limit: 8 } },
  { type: 'default_form', label: 'Formulaire par défaut', icon: 'ri-survey-line', category: 'dynamique', description: 'Le formulaire du marchand, prêt à remplir', defaultProps: {} },
  { type: 'hero_banners', label: 'Bannières', icon: 'ri-slideshow-line', category: 'dynamique', description: 'Bannières défilantes', defaultProps: {} },
  { type: 'contact_form', label: 'Formulaire contact', icon: 'ri-mail-line', category: 'dynamique', description: 'Formulaire de contact', defaultProps: {} },
  { type: 'booking_form', label: 'Formulaire réservation', icon: 'ri-calendar-check-line', category: 'dynamique', description: 'Formulaire de réservation', defaultProps: {} },
  { type: 'marketplace_products', label: 'Marketplace — Produits', icon: 'ri-store-2-line', category: 'marketplace', description: 'Produits de tous les vendeurs', defaultProps: { limit: 12 } },
  { type: 'marketplace_categories', label: 'Marketplace — Catégories', icon: 'ri-apps-line', category: 'marketplace', description: 'Catégories de la marketplace', defaultProps: {} },
  { type: 'marketplace_search', label: 'Marketplace — Recherche', icon: 'ri-search-line', category: 'marketplace', description: 'Barre de recherche marketplace', defaultProps: { placeholder: 'Rechercher...' } },
  { type: 'marketplace_sellers', label: 'Marketplace — Vendeurs', icon: 'ri-user-star-line', category: 'marketplace', description: 'Vendeurs de la marketplace', defaultProps: { limit: 8 } },
];

const BLOCK_TYPES = new Set(BLOCK_CATALOG.map((b) => b.type));

/**
 * Modèles de page « prêts à l'emploi » : une combinaison de blocs
 * applicable en un clic depuis la palette de l'éditeur visuel.
 */
export interface BlockTemplate {
  id: string;
  label: string;
  description: string;
  icon: string;
  blocks: ThemeBlock[];
}

export const BLOCK_TEMPLATES: BlockTemplate[] = [
  {
    id: 'landing',
    label: 'Landing — Hero + Produits + Témoignages + CTA',
    description: 'Page d’atterrissage complète : bannière, produits, avis clients et appel à l’action.',
    icon: 'ri-layout-4-line',
    blocks: [
      { type: 'hero', props: { title: '{{site_name}}', subtitle: '{{site_description}}', ctaLabel: 'Découvrir la boutique', ctaHref: '/produits', cta2Label: 'Nous contacter', cta2Href: '/contact' } },
      { type: 'trust_banner', props: {} },
      { type: 'heading', props: { text: 'Nos produits', level: 2, align: 'center' } },
      { type: 'products', props: { limit: 6 } },
      { type: 'heading', props: { text: 'Ils nous font confiance', level: 2, align: 'center' } },
      { type: 'testimonials', props: {} },
      { type: 'cta', props: { title: 'Prêt à commencer ?', text: 'Découvrez nos offres dès maintenant.', buttonLabel: 'Nous contacter', buttonHref: '/contact' } },
    ],
  },
  {
    id: 'services',
    label: 'Vitrine — Hero + Services + Stats + CTA',
    description: 'Idéal pour un prestataire : présentation, services, chiffres clés et contact.',
    icon: 'ri-briefcase-line',
    blocks: [
      { type: 'hero', props: { title: '{{site_name}}', subtitle: '{{site_description}}', ctaLabel: 'Nos services', ctaHref: '/service', cta2Label: 'Prendre rendez-vous', cta2Href: '/booking' } },
      { type: 'services', props: {} },
      { type: 'stats', props: {} },
      { type: 'tabs', props: { items: [{ title: 'Notre approche', html: '{{site_about}}' }, { title: 'Pourquoi nous', html: '{{site_description}}' }] } },
      { type: 'testimonials', props: {} },
      { type: 'cta', props: { title: 'Discutons de votre projet', text: 'Notre équipe vous répond rapidement.', buttonLabel: 'Nous contacter', buttonHref: '/contact' } },
    ],
  },
  {
    id: 'presentation',
    label: 'Présentation — Hero + À propos + Avant/Après + CTA',
    description: 'Page vitrine narrative avec onglets, comparateur avant/après et appel à l’action.',
    icon: 'ri-image-2-line',
    blocks: [
      { type: 'hero', props: { title: '{{site_name}}', subtitle: '{{site_description}}', ctaLabel: 'En savoir plus', ctaHref: '/apropos' } },
      { type: 'heading', props: { text: 'Notre histoire', level: 2, align: 'center' } },
      { type: 'text', props: { html: '{{site_about}}', align: 'center' } },
      { type: 'before_after', props: { imageBefore: '', imageAfter: '', labelBefore: 'Avant', labelAfter: 'Après' } },
      { type: 'stats', props: {} },
      { type: 'cta', props: { title: 'Envie d’en savoir plus ?', text: 'Contactez-nous pour échanger.', buttonLabel: 'Nous contacter', buttonHref: '/contact' } },
    ],
  },
];

/**
 * Analyse un contenu de page : si c'est un schéma de blocs valide,
 * renvoie l'objet normalisé ; sinon null (→ rendu HTML classique).
 */
export function parseBlockSchema(content: string): ThemeBlockSchema | null {
  const raw = (content || '').trim();
  if (!raw || (raw[0] !== '{' && raw[0] !== '[')) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    let list: unknown[] | null = null;
    if (Array.isArray(parsed)) list = parsed;
    else if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { blocks?: unknown }).blocks)) {
      list = (parsed as { blocks: unknown[] }).blocks;
    }
    if (!list) return null;
    const blocks: ThemeBlock[] = [];
    for (const item of list) {
      if (item && typeof item === 'object' && typeof (item as ThemeBlock).type === 'string') {
        const t = (item as ThemeBlock).type;
        if (BLOCK_TYPES.has(t)) {
          const props = (item as ThemeBlock).props;
          blocks.push({ type: t, props: props && typeof props === 'object' ? props : {} });
        }
      }
    }
    if (blocks.length === 0) return null;
    return { blocks };
  } catch {
    return null;
  }
}

export function isBlockSchema(content: string): boolean {
  return parseBlockSchema(content) !== null;
}

/** Sérialise un schéma de blocs en JSON lisible. */
export function serializeBlockSchema(blocks: ThemeBlock[]): string {
  return JSON.stringify({ blocks }, null, 2);
}